import SV from 'server-vars';
import { includes, hasKey } from '../typescript';
import {
    LOCALE_US,
    LOCALE_GB,
    LOCALE_DE,
    LOCALE_FR,
    LOCALE_IT,
    LOCALE_PSEUDO,
    LOCALE_DEBUG,
    LOCALE_ES,
} from '../locales/constants';
import type { Locale } from '../locales/constants';
import { localeSegmentList, localeSegmentMap } from './constants';
import { isLocale } from '../locales/utils';

const DISALLOWED_STARTING_PATHS = [
    '/soa/',
    /**
     * We don't want to add locale segment to most of our
     * WordPress sites until they can support localized URLs
     */
    '/the-mint/',
];

/**
 * Paths that are not yet translated for certain locales.
 */
const DISALLOWED_STARTING_PATHS_BY_LOCALE: Record<string, string[]> = {
    [LOCALE_IT]: [
        '/info/luxury-report/',
        '/introspective-magazine/',
        '/blogs/the-study/',
        '/1stdibs-50/',
    ],
    [LOCALE_ES]: [
        '/info/luxury-report/',
        '/introspective-magazine/',
        '/blogs/the-study/',
        '/1stdibs-50/',
    ],
};

const WP_SPECIAL_CASES = {
    THE_STUDY: '/blogs/the-study/',
    DIBS_50: '/1stdibs-50/',
};

const WORDPRESS_SPECIAL_CASES = [
    WP_SPECIAL_CASES.THE_STUDY,
    WP_SPECIAL_CASES.DIBS_50,
    '/introspective-magazine/',
    '/about/',
    '/info/',
];

export const GLOBAL_CLIENT_ONLY_LOCALE = 'GLOBAL_CLIENT_ONLY_LOCALE' as const;

function getBuyerHost(): string {
    if (typeof window !== 'undefined') {
        return window.location.origin;
    }
    if (process?.env?.NODE_BUYER_HOST) {
        return process.env.NODE_BUYER_HOST;
    }
    return 'https://www.1stdibs.com';
}

function hasFilenameExtension(segment?: string | null): boolean {
    if (!segment) {
        return false;
    }
    /**
     * If dot is not followed by a digit - we think it's a file extension.
     */
    return /\.[^\d]/.test(segment);
}

function isSupportPageUrl<T extends URL>(url: T): boolean {
    return url.origin.includes('support.1stdibs.com');
}

function isDealerSupportPageUrl<T extends URL>(url: T): boolean {
    return url.origin.includes('sellersupport.1stdibs.com');
}

const supportPageLanguagesMap: Record<Locale, string> = {
    [LOCALE_US]: 'en-us',
    [LOCALE_GB]: 'en-us',
    [LOCALE_DE]: 'de',
    [LOCALE_FR]: 'fr',
    [LOCALE_IT]: 'it',
    [LOCALE_ES]: 'es',
    [LOCALE_PSEUDO]: 'en-us',
    [LOCALE_DEBUG]: 'en-us',
} as const;

function handleSupportPageUrl(url: URL, locale: Locale): string {
    const languageSegment = supportPageLanguagesMap[locale];
    const segmentList = url.pathname.split('/').filter(Boolean);

    const hasLanguageSegment = !!segmentList.find(segment =>
        Object.values(supportPageLanguagesMap).includes(segment)
    );

    if (hasLanguageSegment) {
        // replaces existing language segment with mapped value
        segmentList.splice(1, 1, languageSegment);
    } else {
        // adds language segment
        segmentList.splice(1, 0, languageSegment);
    }

    url.pathname = `/${segmentList.filter(Boolean).join('/')}/`;

    return url.href;
}

function isWpPage(url: URL): boolean {
    return WORDPRESS_SPECIAL_CASES.some(path => {
        return url.pathname.startsWith(path);
    });
}

function handleWordPressPageUrl(
    segmentList: string[],
    localeSegment: string,
    localeSegmentPosition: number
): string {
    if ([localeSegmentMap[LOCALE_GB]].includes(localeSegment)) {
        return `/${segmentList.join('/')}/`;
    }

    const shouldReplaceLocaleSegment = localeSegmentList.some(
        segment => segment === segmentList[localeSegmentPosition]
    );

    if (shouldReplaceLocaleSegment) {
        segmentList[localeSegmentPosition] = localeSegment;
    } else {
        segmentList.splice(localeSegmentPosition, 0, localeSegment);
    }

    return `/${segmentList.filter(Boolean).join('/')}/`;
}

function removeOriginFromHref(url: URL): string {
    return url.href.replace(url.origin, '');
}

type LinkingMode = 'relative' | 'absolute';

function getLinkMode(options?: Options): LinkingMode {
    return options?.intlLinkingMode || SV.get('intlLinkingMode') || 'relative';
}

type Options = {
    intlLinkingMode?: LinkingMode;
};

export function handleLocaleUrl<T>(
    url: T,
    locale: Locale | typeof GLOBAL_CLIENT_ONLY_LOCALE,
    options?: Options
): T | string {
    if (typeof url !== 'string') {
        return url;
    }
    const linkMode = getLinkMode(options);

    const isClient = typeof window !== 'undefined';

    const buyerUrl = getBuyerHost();
    if (!locale || locale === GLOBAL_CLIENT_ONLY_LOCALE) {
        /**
         * In most cases, we'll want to get the locale from server vars, so why not handle it
         * right here. But using server vars on the server is a bad idea, since it's a shared global
         * instance.
         *
         * So we try to catch when you're using handleLocaleUrl on the server and *force* you to
         * pass in a locale query param.
         */
        if (!isClient) {
            if (locale === GLOBAL_CLIENT_ONLY_LOCALE) {
                throw new Error(
                    "You shouldn't use GLOBAL_CLIENT_ONLY_LOCALE on the server. Pass in a proper locale to remove this error."
                );
            }
            throw new Error(
                'You are using `handleLocaleUrl` on the server without the locale argument. Add it as a second argument to remove this error.'
            );
        }

        const serverVarsLocale = SV.get('locale');

        if (isLocale(serverVarsLocale)) {
            locale = serverVarsLocale;
        } else {
            locale = LOCALE_US;
        }
    }

    if (!isLocale(locale)) {
        throw new Error(`Unknown locale ${locale} passed in.`);
    }

    let parsedUrl: URL;

    try {
        parsedUrl = new URL(url, buyerUrl);
    } catch (err) {
        return url;
    }

    const urlStartsWithDisallowedPath = [
        ...DISALLOWED_STARTING_PATHS,
        ...(DISALLOWED_STARTING_PATHS_BY_LOCALE[locale] || []),
    ].some(path => {
        return parsedUrl.pathname.startsWith(path);
    });

    if (urlStartsWithDisallowedPath) {
        return url;
    }

    if (isSupportPageUrl(parsedUrl) || isDealerSupportPageUrl(parsedUrl)) {
        return handleSupportPageUrl(parsedUrl, locale);
    }

    const isAbsoluteBuyerUrl =
        url.includes(parsedUrl.origin) && buyerUrl.includes(parsedUrl.hostname);
    /**
     * Only support absolute buyer urls.
     */
    if (linkMode === 'absolute' && !isAbsoluteBuyerUrl) {
        return url;
    }

    if (linkMode === 'relative' && isAbsoluteBuyerUrl) {
        return url;
    }

    /**
     * We want to ignore path-relative urls. Supporting them with server side rendering is hard,
     * since we dont have access to `document.baseURI` on the server.
     *
     * Exception for absolute buyer urls for the seller side, since absolute urls fall into this
     * case as well.
     */
    if (!url.startsWith('/') && !isAbsoluteBuyerUrl) {
        return url;
    }

    const pathname = parsedUrl.pathname;
    const segmentList = pathname.split('/').filter(Boolean);
    const localeSegment = hasKey(localeSegmentMap, locale) ? localeSegmentMap[locale] : null;

    if (localeSegment === null) {
        throw new Error(
            `Did not find any segment for '${locale}'. Did you pass in the correct locale?`
        );
    }

    if (isWpPage(parsedUrl)) {
        let localeSegmentPosition = 1;

        if (
            url.startsWith(WP_SPECIAL_CASES.THE_STUDY) ||
            (url.startsWith(WP_SPECIAL_CASES.DIBS_50) && segmentList.length > 2)
        ) {
            localeSegmentPosition = 2;
        }

        parsedUrl.pathname = handleWordPressPageUrl(
            segmentList,
            localeSegment,
            localeSegmentPosition
        );
    } else {
        /**
         * Override the locale segment if it exists, otherwise just prepend it.
         */
        if (includes(localeSegmentList, segmentList[0])) {
            segmentList[0] = localeSegment;
        } else if (localeSegment) {
            segmentList.unshift(localeSegment);
        }

        const filteredSegmentList = segmentList.filter(Boolean);

        if (filteredSegmentList.length) {
            parsedUrl.pathname = `/${filteredSegmentList.join('/')}/`;

            /**
             * Attempt to detect whether the last segment is a filename, if so - remove trailing slash.
             */
            const lastSegment = filteredSegmentList.pop();
            if (hasFilenameExtension(lastSegment)) {
                parsedUrl.pathname = parsedUrl.pathname.slice(0, -1);
            }
        } else {
            parsedUrl.pathname = '/';
        }
    }

    /**
     * If an absolute url is passed in - we also want to return an absolute one.
     */
    if (url.includes(parsedUrl.origin)) {
        return parsedUrl.href;
    }

    return removeOriginFromHref(parsedUrl);
}
